import React, { useMemo } from 'react';
import { SidebarAudioButton } from './SidebarAudioButton';
import axiosClient from '@/lib/auth';
import { useCreateAlertNote } from '@/hooks/useCreateAlertNote';
import { FetchSiteDevicesMonitoringQuery } from '@/lib/api/sites/devices';
import { useQuery } from '@tanstack/react-query';
import { InteractionTabProps} from '../types';
import { cn } from '@/lib/utils';
import { Mic, ExternalLink } from 'lucide-react';
import { useAlertEventsCtx } from '../AlertEventsProvider';
import { FetchFullStreamQuery } from '@/lib/api/monitoring/alert';
import { useCurrentTime } from '@/lib/time';
import { Button } from '@/components/ui/button';

export const PushToTalkTab: React.FC<InteractionTabProps> = ({ projectId, companyId, useAlertEvents, alertId, videoProcessorId }) => {

  const devicesQuery = useQuery(
    FetchSiteDevicesMonitoringQuery({
      projectId: projectId!,
      companyId: companyId!,
    })
  );
  const siteDevices = devicesQuery.data;

  const { selectedEvent } = useAlertEventsCtx();
  const now = useCurrentTime(60 * 5 * 1000);
  const streamQuery = useQuery({
    ...FetchFullStreamQuery({
      occurred: selectedEvent?.occurred ? new Date(selectedEvent.occurred) : now,
      companyId,
      projectId,
      videoProcessorIdentifier: selectedEvent?.videoProcessorIdentifier ?? '',
    }),
    staleTime: 30 * 1000,
    enabled: !!selectedEvent?.videoProcessorIdentifier,
  });
  const portalUrl = streamQuery.data?.portalUrl;

  const outputs = useMemo(() => {
    const _devices: { id: string; display: string; emphasised?: boolean }[] = [];

    if (!videoProcessorId || !siteDevices?.devices) return _devices;

    // Get the camera ID from the selected event
    // Now find all other devices' speakers where relatedComponentGroups contains this secondary device ID
    for (const device of siteDevices.devices) {
      const secondaryDevice = device.secondaryDevice;
      if (!secondaryDevice) continue;

      if (secondaryDevice.capabilities?.canTalkdown) {
        _devices.push({
          id: secondaryDevice.id || '',
          display: secondaryDevice.name || '',
          emphasised: secondaryDevice.id === videoProcessorId,
        });
        continue;
      }

      if (secondaryDevice.speakers) {
        const speakers = Array.isArray(secondaryDevice.speakers)
          ? secondaryDevice.speakers
          : [secondaryDevice.speakers];

        speakers.forEach((speaker) => {
          const id = speaker.id || ''; // Use camera ID as the target for talkdown
          const display = `${speaker.name} (${secondaryDevice.name})`;
          _devices.push({
            id,
            display: display,
            emphasised: speaker.id === videoProcessorId,
          });
        });
      }
    }

    return _devices.sort((x) => (x.emphasised ? -1 : 1));
  }, [siteDevices]);
  const defaultValues = useMemo(() => outputs.filter((x) => x.emphasised).map((x) => x.id), [outputs]);

  const createNoteMutation = useCreateAlertNote({
    companyId: companyId!,
    projectId: projectId!,
    alertId: alertId!,
  });

  if (!videoProcessorId || (!alertId && useAlertEvents)) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <div className="mb-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-700">
            <Mic className="h-6 w-6 text-gray-400"  />
          </div>
        </div>
        <h3 className="mb-2 text-lg font-medium text-white">Push to Talk</h3>
      {useAlertEvents ? (
          <p className="text-sm text-gray-400 max-w-sm">
            Select an event to enable push-to-talk functionality. Once an event is selected, you'll be
            able to communicate through connected speakers.
          </p>
        ) : (<p className="text-sm text-gray-400 max-w-sm">
            Select a device to enable push-to-talk functionality. Once a device is selected,  you'll be
            able to communicate through connected speakers.
          </p>
        )}
      </div>
    );
  }

  if (outputs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <div className="mb-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-700">
            <Mic className="h-6 w-6 text-gray-400" />
          </div>
        </div>
     {portalUrl ? (
          <>
            <h3 className="mb-2 text-lg font-medium text-white">External Portal Available</h3>
            <p className="mb-4 max-w-sm text-sm text-gray-400">
              Talkdown might be available through an external portal.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                const windowFeatures =
                  "width=800,height=600,toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes";
                window.open(portalUrl, "_blank", windowFeatures);
              }}
            >
              <ExternalLink className="h-4 w-4" />
              Open External Portal
            </Button>
          </>
       ) : ( 
          <>
            <h3 className="mb-2 text-lg font-medium text-white">No Speakers Available</h3>
            <p className="max-w-sm text-sm text-gray-400">
              No speakers are linked to the currently selected device. Configure speaker-to-camera
              links in the site devices to enable push-to-talk.
            </p>
          </>
        )} 
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <h3 className="mb-2 text-sm font-semibold text-white">Push to Talk</h3>
        <p className="mb-4 text-xs text-gray-400">
          Hold down the button to record and broadcast your message through connected speakers.
        </p>
      </div>

      <SidebarAudioButton
        onTalkdownDone={async (audio: ArrayBuffer, devices) => {
          const searchParams = new URLSearchParams();
          devices!.forEach((id) => searchParams.append('cameraIds', id));
          await axiosClient.post(
            `/company/${companyId}/project/${projectId}/Monitoring/talkdown?${searchParams.toString()}`,
            audio,
            {
              headers: {
                'Content-Type': 'application/octet-stream',
              },
            }
          );
        }}
        onTalkdownEnd={(duration) => {
          const seconds = Math.round(duration / 1000);
          const timestamp = new Date().toLocaleString();
          createNoteMutation.mutate({
            note: `Talk down issued for ${seconds}s at ${timestamp}`,
          });
        }}
        outputs={outputs}
        defaultValue={defaultValues}
      />

      <div className="my-4">
        <h4 className="mb-2 text-xs font-medium text-gray-300">
          Available Speakers ({outputs.length})
        </h4>
        <div className="space-y-1">
          {outputs.map((output) => (
            <div
              key={output.id}
              className={cn(
                'flex items-center rounded bg-gray-800/30 px-2 py-1 text-xs text-gray-400',
                {
                  'bg-white/20 font-bold text-white/90': output.emphasised,
                }
              )}
            >
              <div className="mr-2 h-2 w-2 rounded-full bg-green-400"></div>
              {output.display}{' '}
              {output.emphasised ? <span className="ml-2 text-white">(This camera)</span> : ''}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
